import { Box, Button, Chip, CircularProgress, Stack, Typography } from '@mui/material';
import { CreditCardOutlined as CardIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';

import { dialogOpen, dialogClose } from '@/modules/MuiDialog';
import { formatLocaleNumber } from '@/utils/formatter';
import makeErrorString from '@/utils/makeErrorString';

import { useRefundPayment } from '@/hooks/queries/subscription/useRefundPayment';
import type { SubscriptionPaymentDto } from '@/types/subscription';

import { isRefundable, PAYMENT_STATUS_LABEL, SHOW_REFUND_UI } from './helpers';
import { ACCENT } from '../_shared';

interface NicePayMetadata {
    GoodsName?: string;
    CardName?: string;
    CardNo?: string;
}

const STATUS_COLOR: Record<string, 'default' | 'success' | 'error' | 'warning'> = {
    pending: 'warning',
    success: 'success',
    failed: 'error',
    refunded: 'default',
};

interface PaymentHistoryItemProps {
    payment: SubscriptionPaymentDto;
    planName?: string;
}

/** 친근한 결제 일자 포맷 — 오늘 / 어제 / N일 전 / 절대 날짜 */
function formatPaymentDate(iso: string): string {
    const date = new Date(iso);
    const diffMs = Date.now() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const hh = String(date.getHours()).padStart(2, '0');
    const mm = String(date.getMinutes()).padStart(2, '0');

    if (diffDays === 0) return `오늘 ${hh}:${mm}`;
    if (diffDays === 1) return `어제 ${hh}:${mm}`;
    if (diffDays < 7) return `${diffDays}일 전`;
    return `${date.getFullYear()}년 ${date.getMonth() + 1}월 ${date.getDate()}일`;
}

/** NicePay 마스킹 카드번호에서 끝 4자리 추출 ("46020500****9072" → "9072") */
function extractCardLast4(cardNo?: string): string | null {
    if (!cardNo) return null;
    const match = cardNo.match(/(\d{4})$/);
    return match ? match[1] : null;
}

const PaymentHistoryItem = ({ payment, planName }: PaymentHistoryItemProps) => {
    const refund = useRefundPayment();
    const canRefund = SHOW_REFUND_UI && isRefundable(payment);

    const metadata = (payment.metadata as NicePayMetadata | null) ?? {};
    const title = metadata.GoodsName || planName || '구독 결제';
    const cardName = metadata.CardName;
    const cardLast4 = extractCardLast4(metadata.CardNo);
    const dateLabel = formatPaymentDate(payment.paidAt ?? payment.createdAt);
    const isRefunded = payment.status === 'refunded';
    const isFailed = payment.status === 'failed';
    const showStatusChip = payment.status !== 'success'; // 성공은 굳이 칩 안 보임

    const handleRefund = () => {
        dialogOpen({
            title: '결제를 환불할까요?',
            message: `₩${formatLocaleNumber(payment.amount)} 가 카드사로 환불 처리됩니다. 결제일로부터 14일 이내만 가능해요.`,
            confirmText: '환불 요청',
            confirmButtonProps: { color: 'error' as any },
            onConfirmed: async () => {
                try {
                    await refund.mutateAsync(payment.id);
                    toast.success('환불을 처리했어요');
                    dialogClose();
                } catch (e) {
                    toast.error(makeErrorString(e, '환불에 실패했어요'));
                }
            },
        });
    };

    return (
        <Stack
            direction="row"
            spacing={1.75}
            alignItems="center"
            sx={{
                py: 2,
                borderBottom: '1px solid',
                borderColor: 'divider',
                '&:last-of-type': { borderBottom: 'none' },
            }}
        >
            {/* 좌측 카드 아이콘 */}
            <Box
                sx={{
                    width: 38,
                    height: 38,
                    borderRadius: 1.5,
                    bgcolor: 'rgba(0,0,0,0.04)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: isRefunded || isFailed ? 'text.disabled' : ACCENT,
                    flexShrink: 0,
                }}
            >
                <CardIcon fontSize="small" />
            </Box>

            {/* 본문 — 제목 + 카드/날짜 */}
            <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 700,
                            color: isRefunded ? 'text.disabled' : 'text.primary',
                            textDecoration: isRefunded ? 'line-through' : 'none',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {title}
                    </Typography>
                    {showStatusChip && (
                        <Chip
                            size="small"
                            label={PAYMENT_STATUS_LABEL[payment.status] ?? payment.status}
                            color={STATUS_COLOR[payment.status] ?? 'default'}
                            sx={{ fontWeight: 700, height: 18, fontSize: 10, '& .MuiChip-label': { px: 0.75 } }}
                        />
                    )}
                </Stack>
                <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    sx={{ color: 'text.secondary' }}
                    divider={
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.disabled', lineHeight: 1 }}
                        >
                            ·
                        </Typography>
                    }
                >
                    {cardName && (
                        <Typography variant="caption" sx={{ color: 'inherit' }}>
                            {cardName}
                            {cardLast4 ? ` ・・・・ ${cardLast4}` : ''}
                        </Typography>
                    )}
                    <Typography variant="caption" sx={{ color: 'inherit' }}>
                        {dateLabel}
                    </Typography>
                </Stack>
            </Box>

            {/* 우측 금액 + 환불 버튼 */}
            <Stack
                alignItems="flex-end"
                spacing={0.25}
                sx={{ flexShrink: 0, minWidth: 80 }}
            >
                <Typography
                    sx={{
                        fontWeight: 800,
                        fontSize: 15,
                        letterSpacing: '-0.01em',
                        color: isRefunded ? 'text.disabled' : 'text.primary',
                        textDecoration: isRefunded ? 'line-through' : 'none',
                        fontVariantNumeric: 'tabular-nums',
                    }}
                >
                    ₩{formatLocaleNumber(payment.amount)}
                </Typography>
                {canRefund && (
                    <Button
                        size="small"
                        onClick={handleRefund}
                        disabled={refund.isPending}
                        variant="text"
                        startIcon={
                            refund.isPending ? (
                                <CircularProgress size={11} color="inherit" />
                            ) : undefined
                        }
                        sx={{
                            mt: 0,
                            p: 0,
                            minWidth: 0,
                            fontSize: 11,
                            fontWeight: 600,
                            textTransform: 'none',
                            cursor: 'pointer',
                            color: 'text.secondary',
                            lineHeight: 1.2,
                            '&:hover': { color: 'error.main', bgcolor: 'transparent' },
                            '&.Mui-disabled': { color: 'text.disabled' },
                        }}
                    >
                        환불 요청
                    </Button>
                )}
                {isRefunded && payment.refundedAt && (
                    <Typography variant="caption" sx={{ fontSize: 10, color: 'text.disabled' }}>
                        {formatPaymentDate(payment.refundedAt)} 환불
                    </Typography>
                )}
            </Stack>
        </Stack>
    );
};

export default PaymentHistoryItem;
